import { useMemo, useState } from "react";

import { HS_NONE_MATCH_COPY, HS_UNCONFIRMED_WARNING } from "../constants";
import type { HsCandidate, HsCandidateGroup } from "../types";

interface Props {
  groups: HsCandidateGroup[];
  onSubmit: (message: string) => void;
  disabled?: boolean;
}

const CandidateButton = ({
  candidate,
  selected,
  disabled,
  onClick,
}: {
  candidate: HsCandidate;
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    aria-pressed={selected}
    className={`tw-w-full tw-rounded-lg tw-border tw-px-3 tw-py-2 tw-text-left tw-transition-colors disabled:tw-cursor-not-allowed disabled:tw-opacity-60 ${
      selected ? "tw-border-[#4F46E5] tw-bg-[#EEF0FE]" : "tw-border-[#E2E2F0] tw-bg-white hover:tw-bg-[#FAFAFE]"
    }`}
  >
    <div className="tw-flex tw-items-baseline tw-gap-2">
      <span className="tw-font-mono tw-text-label-sm tw-font-semibold tw-text-[#1E1B4B]">{candidate.code}</span>
      {selected && <span className="tw-text-label-xs tw-font-medium tw-text-[#4F46E5]">Selected</span>}
    </div>
    <div className="tw-mt-0.5 tw-text-label-xs tw-leading-relaxed tw-text-gray-600">{candidate.description}</div>
  </button>
);

const HsCandidates = ({ groups, onSubmit, disabled }: Props) => {
  const [picks, setPicks] = useState<Record<string, string>>({});
  const multiProduct = groups.length > 1;

  const grouped = useMemo(
    () =>
      groups.map((group) => ({
        ...group,
        confirmed: group.candidates.filter((c) => c.confirmed),
        unconfirmed: group.candidates.filter((c) => !c.confirmed),
      })),
    [groups]
  );

  const allPicked = grouped.every((group) => picks[group.product]);

  const choose = (product: string, code: string) => {
    if (disabled) return;
    if (!multiProduct) {
      onSubmit(code);
      return;
    }
    setPicks((prev) => ({ ...prev, [product]: code }));
  };

  const submitAll = () => {
    if (disabled || !allPicked) return;
    onSubmit(grouped.map((group) => `${group.product}: ${picks[group.product]}`).join("; "));
  };

  return (
    <div className="tw-mt-3 tw-space-y-4">
      {grouped.map((group) => (
        <div key={group.product}>
          <div className="tw-mb-2 tw-text-label-sm tw-font-semibold tw-text-[#1E1B4B]">{group.product}</div>

          {group.confirmed.length > 0 && (
            <div className="tw-space-y-1.5">
              {group.confirmed.map((candidate) => (
                <CandidateButton
                  key={candidate.code}
                  candidate={candidate}
                  disabled={disabled}
                  selected={picks[group.product] === candidate.code}
                  onClick={() => choose(group.product, candidate.code)}
                />
              ))}
            </div>
          )}

          {group.unconfirmed.length > 0 && (
            <div className="tw-mt-3 tw-rounded-lg tw-border tw-border-warning-200 tw-bg-warning-50 tw-p-2.5">
              <div className="tw-mb-2 tw-flex tw-gap-2">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true" className="tw-mt-0.5 tw-shrink-0">
                  <path d="M8 2.2 14.6 13.4H1.4Z" stroke="#B45309" strokeWidth="1.3" strokeLinejoin="round" />
                  <path d="M8 6.6v3M8 11.4h.01" stroke="#B45309" strokeWidth="1.4" strokeLinecap="round" />
                </svg>
                <div className="tw-text-label-xs tw-leading-relaxed tw-text-[#92610A]">{HS_UNCONFIRMED_WARNING}</div>
              </div>
              <div className="tw-space-y-1.5">
                {group.unconfirmed.map((candidate) => (
                  <CandidateButton
                    key={candidate.code}
                    candidate={candidate}
                    disabled={disabled}
                    selected={picks[group.product] === candidate.code}
                    onClick={() => choose(group.product, candidate.code)}
                  />
                ))}
              </div>
            </div>
          )}

          {group.ifNoneMatch && (
            <div className="tw-mt-2 tw-text-label-xs tw-leading-relaxed tw-text-gray-600">
              <span className="tw-font-medium">{HS_NONE_MATCH_COPY.prompt}</span>{" "}
              {HS_NONE_MATCH_COPY.supplyExact} in the box below, or ask me to {HS_NONE_MATCH_COPY.runClassifier.toLowerCase()}.{" "}
              <span className="tw-text-gray-500">{HS_NONE_MATCH_COPY.classifierNote}</span>
            </div>
          )}
        </div>
      ))}

      {multiProduct && (
        <button
          type="button"
          onClick={submitAll}
          disabled={disabled || !allPicked}
          className="tw-w-full tw-rounded-lg tw-bg-[#4F46E5] tw-px-4 tw-py-2 tw-text-label-sm tw-font-medium tw-text-white hover:tw-bg-[#312E81] disabled:tw-cursor-not-allowed disabled:tw-bg-[#C7CDFB]"
        >
          {allPicked ? "Continue" : `Pick a code for each product (${Object.keys(picks).length}/${grouped.length})`}
        </button>
      )}
    </div>
  );
};

export default HsCandidates;
